import React, { useState, useEffect } from "react";
import {
    Box, Typography, Paper, Tabs, Tab, TextField, Button,
    CircularProgress, Switch, FormControlLabel, Alert, Snackbar,
    Divider, Chip, InputAdornment
} from "@mui/material";
import {
    Save, Loyalty, SettingsInputComponent, TwoWheeler, DirectionsCar,
    Code as CodeIcon, Percent as PercentIcon, AccessTime as TimeIcon
} from "@mui/icons-material";

// Services
import {
    getRetentionProgram, updateRetentionProgram,
    getAppConfigs, updateAppConfig
} from "../services/businessSettingsService";

// Types
import { RetentionProgram, AppConfig } from "../types/BusinessSettings";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
            style={{ width: '100%' }}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const BusinessSettings: React.FC = () => {
    const [mainTabIndex, setMainTabIndex] = useState(0);
    const [subTabIndex, setSubTabIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // State for each section
    const [retentionPrograms, setRetentionPrograms] = useState<RetentionProgram[]>([]);
    const [configs, setConfigs] = useState<AppConfig[]>([]);

    // Fetch All Data on Mount
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [retRes, confRes] = await Promise.all([
                getRetentionProgram(),
                getAppConfigs()
            ]);

            // Axios response handling
            setRetentionPrograms(retRes.data);
            setConfigs(confRes.data);
        } catch (error) {
            console.error("Failed to load settings", error);
            setMessage({ type: 'error', text: 'Failed to load business settings.' });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveRetention = async (program: RetentionProgram) => {
        let payload = { ...program };

        if (program.targetRole === 'driver' && typeof program.customSchedule === 'string') {
            try {
                // Try parsing the text input back to JSON object
                const parsed = JSON.parse(program.customSchedule);
                payload.customSchedule = parsed;
            } catch (e) {
                setMessage({ type: 'error', text: 'Invalid JSON format in Stepped Schedule' });
                return; // Stop save
            }
        }

        try {
            await updateRetentionProgram(program.id, payload);
            setMessage({ type: 'success', text: `${program.targetRole.toUpperCase()} program updated successfully.` });
            fetchData();
        } catch (e) {
            setMessage({ type: 'error', text: 'Failed to update retention program.' });
        }
    };

    const handleSaveConfig = async (config: AppConfig) => {
        if (config.key === 'RIDER_SUBSIDY_CONFIG') {
            try {
                JSON.parse(config.value);
            } catch (e) {
                setMessage({ type: 'error', text: `Invalid JSON format for ${config.key}` });
                return; // Stop execution
            }
        }

        try {
            await updateAppConfig(config.id, { value: config.value });
            setMessage({ type: 'success', text: `Updated ${config.key}` });
        } catch (e) {
            setMessage({ type: 'error', text: `Failed to update ${config.key}` });
        }
    };

    // Helper to update local state for specific program in the array
    const updateRetentionField = (id: number, field: keyof RetentionProgram, value: any) => {
        setRetentionPrograms(prev => prev.map(p =>
            p.id === id ? { ...p, [field]: value } : p
        ));
    };

    const updateConfigValue = (id: number, newValue: string) => {
        setConfigs(prev => prev.map(c => c.id === id ? { ...c, value: newValue } : c));
    };

    // Helper to render the form for a specific program
    const renderRetentionForm = (role: 'driver' | 'rider') => {
        // Find the specific program for this role (assuming 1 active for now, or take first)
        const program = retentionPrograms.find(p => p.targetRole === role);

        if (!program) {
            return (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                        No {role} program found. Please run the seed script.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {role === 'driver' ? 'Driver Incentive' : 'Rider Loyalty'} Configuration
                    </Typography>
                    <Chip
                        label={program.isActive ? "Active" : "Inactive"}
                        color={program.isActive ? "success" : "default"}
                        size="small"
                    />
                </Box>

                <TextField
                    label="Program Name"
                    value={program.name}
                    onChange={e => updateRetentionField(program.id, 'name', e.target.value)}
                    fullWidth
                    size="small"
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Start Amount"
                        type="number"
                        value={program.startAmount}
                        onChange={e => updateRetentionField(program.id, 'startAmount', Number(e.target.value))}
                        fullWidth
                        size="small"
                        InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>₹</Box> }}
                        helperText={role === 'driver' ? "Fallback if schedule missing" : "Fixed bonus amount"}
                    />
                    <TextField
                        label="Reduction Per Week"
                        type="number"
                        value={program.reductionAmount}
                        onChange={e => updateRetentionField(program.id, 'reductionAmount', Number(e.target.value))}
                        fullWidth
                        size="small"
                        InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>₹</Box> }}
                        helperText={role === 'rider' ? "Set to 0 for fixed bonus" : "Fallback Decay"}
                        disabled={role === 'rider'}
                    />
                </Box>

                {/* Advanced Stepped Logic (Driver Only) */}
                {role === 'driver' && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fdfdfd' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CodeIcon fontSize="small" color="primary"/> Advanced Stepped Schedule
                        </Typography>
                        <TextField
                            label="Custom JSON Schedule"
                            multiline
                            minRows={3}
                            fullWidth
                            size="small"
                            // If object (from DB), stringify. If string (user typing), show as is.
                            value={
                                typeof program.customSchedule === 'object' && program.customSchedule !== null
                                    ? JSON.stringify(program.customSchedule, null, 2)
                                    : program.customSchedule || ''
                            }
                            onChange={e => updateRetentionField(program.id, 'customSchedule', e.target.value)}
                            helperText='Example: {"1": 100, "2": 150, "9": 300}'
                            sx={{ fontFamily: 'monospace' }}
                        />
                    </Paper>
                )}

                {/* Coin Cap Logic (Driver Only) */}
                {role === 'driver' && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff8f0', borderColor: '#ffe0b2' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#e65100' }}>
                            <PercentIcon fontSize="small" /> Coin Usage Caps
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Cap Applies After (Months)"
                                type="number"
                                value={program.coinCapMonths || 0}
                                onChange={e => updateRetentionField(program.id, 'coinCapMonths', Number(e.target.value))}
                                fullWidth
                                size="small"
                                InputProps={{ startAdornment: <InputAdornment position="start"><TimeIcon fontSize="small"/></InputAdornment> }}
                            />
                            <TextField
                                label="Max Coin Payment (%)"
                                type="number"
                                value={program.coinCapPercent || 0}
                                onChange={e => updateRetentionField(program.id, 'coinCapPercent', Number(e.target.value))}
                                fullWidth
                                size="small"
                                helperText="e.g. 0.5 for 50%"
                                InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                            />
                        </Box>
                    </Paper>
                )}

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Duration (Weeks)"
                        type="number"
                        value={program.durationWeeks}
                        onChange={e => updateRetentionField(program.id, 'durationWeeks', Number(e.target.value))}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Interval (Days)"
                        type="number"
                        value={program.intervalDays}
                        onChange={e => updateRetentionField(program.id, 'intervalDays', Number(e.target.value))}
                        fullWidth
                        size="small"
                        helperText="Payout frequency (Default: 7)"
                    />
                </Box>

                <FormControlLabel
                    control={
                        <Switch
                            checked={program.isActive}
                            onChange={e => updateRetentionField(program.id, 'isActive', e.target.checked)}
                        />
                    }
                    label={`Enable ${role.charAt(0).toUpperCase() + role.slice(1)} Program`}
                />

                <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => handleSaveRetention(program)}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    Save {role === 'driver' ? 'Driver' : 'Rider'} Program
                </Button>
            </Box>
        );
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;

    return (
        <Box sx={{
            maxWidth: "800px",
            width: "100%",
            margin: '0 auto',
            p: 2
        }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                Business Logic Settings
            </Typography>

            <Paper sx={{ width: '100%', minHeight: '500px' }}>
                <Tabs
                    value={mainTabIndex}
                    onChange={(_, v) => setMainTabIndex(v)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab icon={<Loyalty />} label="Retention Bonus" />
                    <Tab icon={<SettingsInputComponent />} label="Wallet Configs" />
                </Tabs>

                {/* --- TAB 1: RETENTION (Index 0) --- */}
                <TabPanel value={mainTabIndex} index={0}>
                    <Typography variant="h6" gutterBottom>Retention Programs</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Configure loyalty bonuses for Drivers and Riders.
                    </Typography>

                    {/* NESTED SUB-TABS */}
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Tabs
                            value={subTabIndex}
                            onChange={(_, v) => setSubTabIndex(v)}
                            indicatorColor="secondary"
                            textColor="secondary"
                            variant="fullWidth"
                            sx={{ bgcolor: '#f9f9f9', borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab icon={<DirectionsCar fontSize="small" />} label="Driver Program" iconPosition="start" />
                            <Tab icon={<TwoWheeler fontSize="small" />} label="Rider Program" iconPosition="start" />
                        </Tabs>

                        {/* SUB-TAB 0: DRIVER */}
                        {subTabIndex === 0 && (
                            <Box sx={{ p: 3 }}>
                                {renderRetentionForm('driver')}
                            </Box>
                        )}

                        {/* SUB-TAB 1: RIDER */}
                        {subTabIndex === 1 && (
                            <Box sx={{ p: 3 }}>
                                {renderRetentionForm('rider')}
                            </Box>
                        )}
                    </Box>
                </TabPanel>

                {/* --- TAB 2: WALLET CONFIGS (Index 1) --- */}
                <TabPanel value={mainTabIndex} index={1}>
                    <Typography variant="h6" gutterBottom>Wallet & Global Configs</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Manage thresholds for driver wallet blocking and other global constants.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {configs.map((conf) => {
                            // Check if this is the sensitive JSON config
                            const isJsonConfig = conf.key === 'RIDER_SUBSIDY_CONFIG';

                            return (
                                <Paper
                                    key={conf.id}
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        // If JSON, stack vertically. If simple, keep row.
                                        flexDirection: isJsonConfig ? 'column' : 'row',
                                        alignItems: isJsonConfig ? 'flex-start' : 'center',
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ flex: 1, width: isJsonConfig ? '100%' : 'auto' }}>
                                        <Typography variant="subtitle1" fontWeight="bold">{conf.key}</Typography>
                                        <Typography variant="caption" color="textSecondary">{conf.description || "No description"}</Typography>
                                    </Box>

                                    {/* Wrapper for Input + Button to handle layout without Grid */}
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 2,
                                        flexDirection: isJsonConfig ? 'column' : 'row',
                                        alignItems: isJsonConfig ? 'flex-end' : 'center',
                                        width: isJsonConfig ? '100%' : 'auto'
                                    }}>
                                        <TextField
                                            size="small"
                                            label={isJsonConfig ? "JSON Logic" : "Value"}
                                            value={conf.value}
                                            onChange={(e) => updateConfigValue(conf.id, e.target.value)}
                                            multiline={isJsonConfig}
                                            minRows={isJsonConfig ? 4 : 1}
                                            sx={{
                                                width: isJsonConfig ? '100%' : 150,
                                                fontFamily: isJsonConfig ? 'monospace' : 'inherit',
                                                '& textarea': { fontFamily: 'monospace', fontSize: '0.875rem' }
                                            }}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => handleSaveConfig(conf)}
                                        >
                                            Update
                                        </Button>
                                    </Box>
                                </Paper>
                            );
                        })}
                        {configs.length === 0 && <Typography>No configs found.</Typography>}
                    </Box>
                </TabPanel>
            </Paper>

            {/* Feedback Snackbar */}
            <Snackbar
                open={!!message}
                autoHideDuration={4000}
                onClose={() => setMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={message?.type} onClose={() => setMessage(null)}>
                    {message?.text}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BusinessSettings;